import { Navigate, Route, Routes } from "react-router";

import { ROUTES } from "@/constants/routes";
import { ProtectedLayout } from "@/features/auth/auth-guard";
import { GlobalAccessGate } from "@/layouts/GlobalAccessGate";
import { OrganizationGate } from "@/layouts/OrganizationGate";
import { ShadowLayout } from "@/layouts/ShadowLayout";
import { Costs } from "@/pages/Costs";
import { EventDetail } from "@/pages/events/EventDetail";
import { Events } from "@/pages/events/Events";
import { GoldVideos } from "@/pages/gold-videos/GoldVideos";
import { LocationManagement } from "@/pages/LocationManagement";
import { Login } from "@/pages/Login";
import { LookoutInstallations } from "@/pages/LookoutInstallations";
import { NotFound } from "@/pages/NotFound";
import { Calibration } from "@/pages/plausibility/Calibration";
import { GlobalPriors } from "@/pages/plausibility/GlobalPriors";
import { PlausibilitySettings } from "@/pages/plausibility/PlausibilitySettings";
import { Replay } from "@/pages/plausibility/Replay";
import { SensorSpecs } from "@/pages/plausibility/taxonomy/SensorSpecs";
import { Taxonomy } from "@/pages/plausibility/taxonomy/Taxonomy";
import { TenantPriors } from "@/pages/plausibility/TenantPriors";
import { UnclassifiedAlerts } from "@/pages/plausibility/UnclassifiedAlerts";
import { VerificationDetail } from "@/pages/plausibility/VerificationDetail";
import { Verifications } from "@/pages/plausibility/Verifications";
import { Reanalyses } from "@/pages/reanalysis/Reanalyses";
import { ReanalysisEvent } from "@/pages/reanalysis/ReanalysisEvent";
import { SceneProposalDetail } from "@/pages/scene-facts/SceneProposalDetail";
import { SceneProposals } from "@/pages/scene-facts/SceneProposals";
import { TrackDetail } from "@/pages/tracks/TrackDetail";
import { VisionLab } from "@/pages/vision-lab/VisionLab";

/**
 * One shell, two gates. Organization pages sit behind `OrganizationGate`
 * because they act on the selected organization; the verification,
 * scene-proposal and reanalysis pages are organization-scoped too but
 * internal-only, so they sit behind both gates. Global pages need only the
 * grant.
 */
export const AppRoutes = () => (
	<Routes>
		<Route element={<Login />} path={ROUTES.LOGIN} />

		<Route element={<ProtectedLayout />}>
			<Route element={<ShadowLayout />}>
				<Route
					element={<Navigate replace to={ROUTES.ORGANIZATION_LOCATIONS} />}
					index
				/>
				<Route
					element={<Navigate replace to={ROUTES.ORGANIZATION_LOCATIONS} />}
					path={ROUTES.ORGANIZATION}
				/>
				<Route
					element={<Navigate replace to={ROUTES.GLOBAL_UNCLASSIFIED} />}
					path={ROUTES.GLOBAL}
				/>

				<Route element={<OrganizationGate />}>
					<Route
						element={<Navigate replace to={ROUTES.ORGANIZATION_LOCATIONS} />}
						path="/organization/map"
					/>
					<Route
						element={<LocationManagement />}
						path={ROUTES.ORGANIZATION_LOCATIONS}
					/>
					<Route
						element={<LocationManagement />}
						path={ROUTES.ORGANIZATION_LOCATION}
					/>
					<Route
						element={<LookoutInstallations />}
						path={ROUTES.ORGANIZATION_INSTALLATIONS}
					/>
					<Route
						element={<PlausibilitySettings />}
						path={ROUTES.ORGANIZATION_PLAUSIBILITY_SETTINGS}
					/>
					<Route element={<TenantPriors />} path={ROUTES.ORGANIZATION_PRIORS} />

					<Route element={<GlobalAccessGate />}>
						<Route element={<Events />} path={ROUTES.ORGANIZATION_EVENTS} />
						<Route element={<EventDetail />} path={ROUTES.ORGANIZATION_EVENT} />
						<Route element={<TrackDetail />} path={ROUTES.ORGANIZATION_TRACK} />
						<Route
							element={<Verifications />}
							path={ROUTES.ORGANIZATION_VERIFICATIONS}
						/>
						<Route
							element={<VerificationDetail />}
							path={ROUTES.ORGANIZATION_VERIFICATION}
						/>
						<Route
							element={<SceneProposals />}
							path={ROUTES.ORGANIZATION_SCENE_PROPOSALS}
						/>
						<Route
							element={<SceneProposalDetail />}
							path={ROUTES.ORGANIZATION_SCENE_PROPOSAL}
						/>
						<Route
							element={<Reanalyses />}
							path={ROUTES.ORGANIZATION_REANALYSIS}
						/>
						<Route
							element={<ReanalysisEvent />}
							path={ROUTES.ORGANIZATION_REANALYSIS_EVENT}
						/>
						<Route element={<Replay />} path={ROUTES.ORGANIZATION_REPLAY} />
						<Route element={<Costs />} path={ROUTES.ORGANIZATION_COSTS} />
						<Route
							element={<SensorSpecs />}
							path={ROUTES.ORGANIZATION_CAMERA_FEATURES}
						/>
					</Route>
				</Route>

				<Route element={<GlobalAccessGate />}>
					<Route
						element={<UnclassifiedAlerts />}
						path={ROUTES.GLOBAL_UNCLASSIFIED}
					/>
					<Route element={<Taxonomy />} path={ROUTES.GLOBAL_TAXONOMY} />
					<Route element={<GlobalPriors />} path={ROUTES.GLOBAL_PRIORS} />
					<Route element={<Calibration />} path={ROUTES.GLOBAL_CALIBRATION} />
					<Route element={<GoldVideos />} path={ROUTES.GLOBAL_GOLD_VIDEOS} />
					<Route element={<VisionLab />} path={ROUTES.GLOBAL_VISION_LAB} />
				</Route>
			</Route>
		</Route>

		<Route element={<NotFound />} path={ROUTES.NOT_FOUND} />
	</Routes>
);
